import { useQuery } from '@tanstack/react-query';

import { manifestSummaryFor } from '@/pages/workflow/helper/custom-code-manifest';
import {
  getNodeCode,
  NodeCodeError,
  type NodeManifestSummary,
} from '@/services/workflow/node-code';

export const nodeManifestQueryKey = (
  orgId: string,
  workflowId: string,
  nodeId: string,
  scriptPath: string,
  scriptSha: string,
) =>
  [
    'node-code-manifest',
    orgId,
    workflowId,
    nodeId,
    scriptPath,
    scriptSha,
  ] as const;

export interface NodeManifestState {
  summary: NodeManifestSummary | null;
  /** Pinned SHA as the backend resolved it (empty when unpinned). */
  resolvedSha: string;
  pinned: boolean;
  workflowVersionNumber: number | null;
  nodeDisplayName: string;
  canEdit: boolean;
}

/**
 * The Script card's data: the node's pinned `node.json` read through the
 * code-read route and summarised (server-side when the backend sends
 * `manifest`, client-side otherwise). The pin (script_path + script_sha) is
 * part of the key, so a repin or a path edit refetches on its own.
 */
export function useNodeManifest(
  orgId: string | undefined,
  workflowId: string | undefined,
  nodeId: string,
  scriptPath: string,
  scriptSha: string,
  enabled = true,
) {
  const persisted = Boolean(orgId && workflowId && workflowId !== 'new');
  return useQuery<NodeManifestState, NodeCodeError>({
    queryKey: nodeManifestQueryKey(
      orgId ?? '',
      workflowId ?? '',
      nodeId,
      scriptPath,
      scriptSha,
    ),
    enabled: enabled && persisted,
    queryFn: async ({ signal }) => {
      const response = await getNodeCode(orgId!, workflowId!, nodeId, signal);
      return {
        summary: manifestSummaryFor(response),
        resolvedSha: response.resolved_sha || response.pinned_sha || '',
        pinned: response.pinned,
        workflowVersionNumber: response.workflow_version_number ?? null,
        nodeDisplayName: response.node_display_name,
        canEdit: response.can_edit,
      };
    },
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}
